import React, { useContext, useEffect, useRef, useState } from 'react';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';
import sessionsService from '../../../services/sessionsServices';
import { TemporaryChatContext } from '../../../Context/temporaryChatContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, MoreVertical } from 'lucide-react';
import ModalContainer from '../../ModalContainer';
import RenameChatModal from '../RenameChatModal';
import DeleteChatModal from '../DeleteChatModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from '../../Toast';
import { useSidebarSessions, sessionsInvalidateKey } from '../../../hooks';
import { DEFAULT_COPILOT_ID } from '../../../constants/defaultCopilot';
import { cn } from '../../../lib/utils';

const SessionsListSection = ({ selectedAgent }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  const dropdownRef = useRef(null);
  const { toggleRef } = useContext(TemporaryChatContext);
  const payload = {
    platform_id: 'dugout_labs',
    agent_id: selectedAgent?.value,
    project_id: 'test',
  };

  const { data, isPending } = useSidebarSessions(payload);
  const [chatDropdownOpen, setChatDropdownOpen] = useState(null);
  const [deleteChat, setDeleteChat] = useState(null);

  const resolveAgentId = chat => chat?.agent_id || selectedAgent?.value || DEFAULT_COPILOT_ID;

  const handleAddFavorite = useMutation({
    mutationFn: async chat => {
      return await sessionsService?.updateSession({
        platform_id: 'dugout_labs',
        session_id: chat?.id,
        agent_id: resolveAgentId(chat),
        is_favorite: true,
      });
    },
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  const handleRemoveFavorite = useMutation({
    mutationFn: async chat => {
      return await sessionsService?.updateSession({
        platform_id: 'dugout_labs',
        session_id: chat?.id,
        agent_id: resolveAgentId(chat),
        is_favorite: false,
      });
    },
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setChatDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const Timeline = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days'];

  const renderChatItem = (chat, { favorite }) => {
    const isActive = location.pathname?.includes(chat?.id);
    return (
      <li
        key={chat.id}
        ref={chatDropdownOpen === chat.id ? dropdownRef : null}
        className={cn(
          'group relative flex items-center gap-1 rounded-md transition-colors',
          isActive ? 'bg-muted text-ink' : 'text-mute hover:bg-muted/60 hover:text-ink',
        )}>
        {isActive ? (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary rounded-r" />
        ) : null}
        <button
          type="button"
          className="min-w-0 flex-1 truncate px-3 py-2 text-left text-[13px] font-medium"
          onClick={() => {
            toggleRef(true);
            navigate(`/chat/${chat?.id}`);
          }}
          dangerouslySetInnerHTML={{ __html: chat?.title }}
        />
        <Menu
          menuButton={
            <MenuButton
              className={cn(
                'mr-1 flex-shrink-0 rounded p-1 bg-transparent border-0 cursor-pointer transition-opacity',
                'text-mute hover:text-ink',
                isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
              )}
              aria-label="Chat actions">
              <MoreVertical className="w-3.5 h-3.5" />
            </MenuButton>
          }
          align="end"
          direction="bottom"
          position="anchor"
          viewScroll="close"
          menuClassName="!bg-card !border !border-shell-border !rounded-xl !shadow-soft !py-1 !min-w-[140px] !z-50">
          <MenuItem
            className="!text-mute hover:!bg-muted hover:!text-ink !text-sm !px-3 !py-2 !cursor-pointer"
            onClick={() =>
              favorite ? handleRemoveFavorite.mutate(chat) : handleAddFavorite.mutate(chat)
            }>
            {favorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </MenuItem>
          <ModalContainer
            width="600"
            title="Edit Chat Title"
            content={({ onClose }) => <RenameChatModal onClose={onClose} data={chat} />}
            btnComponent={({ onClick }) => (
              <MenuItem
                className="!text-mute hover:!bg-muted hover:!text-ink !text-sm !px-3 !py-2 !cursor-pointer"
                onClick={onClick}>
                Rename
              </MenuItem>
            )}
          />
          <MenuItem
            className="!text-[#D85B5B] hover:!bg-[#D85B5B]/10 !text-sm !px-3 !py-2 !cursor-pointer"
            onClick={() => setDeleteChat(chat)}>
            Delete
          </MenuItem>
        </Menu>
      </li>
    );
  };

  const SectionLabel = ({ children }) => (
    <span className="block px-3 mb-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-mute">
      {children}
    </span>
  );

  if (isPending) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
      </div>
    );
  }

  const hasAny =
    (data?.sessions?.Favorite?.length || 0) > 0 ||
    Timeline.some(time => (data?.sessions?.[time]?.length || 0) > 0);

  if (!hasAny) {
    return <p className="px-3 py-4 text-xs text-mute">No chats yet.</p>;
  }

  return (
    <div className="space-y-3">
      {data?.sessions?.Favorite?.length > 0 && (
        <div>
          <SectionLabel>Favorites</SectionLabel>
          <ul className="space-y-0.5 list-none m-0 p-0">
            {data.sessions.Favorite.map(chat => renderChatItem(chat, { favorite: true }))}
          </ul>
        </div>
      )}

      {Timeline.map(time =>
        data?.sessions?.[time]?.length > 0 ? (
          <div key={time}>
            <SectionLabel>{time}</SectionLabel>
            <ul className="space-y-0.5 list-none m-0 p-0">
              {data.sessions[time].map(chat => renderChatItem(chat, { favorite: false }))}
            </ul>
          </div>
        ) : null,
      )}

      {deleteChat && <DeleteChatModal onClose={() => setDeleteChat(null)} data={deleteChat} />}
    </div>
  );
};

export default SessionsListSection;
